import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Button } from "../ui/button";
import { useLead } from "../LeadDialog";
import { Starburst } from "../common";
import { IMAGES, BADGES } from "../../data/site";

const HERO_LINES = ["Cleaning You", "Can Count On."];
const ease = [0.76, 0, 0.24, 1];

const Hero = () => {
  const { openLead } = useLead();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const starY = useTransform(scrollYProgress, [0, 1], ["0%", "120%"]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden"
      data-testid="hero-section"
    >
      {/* floating stars */}
      <motion.div style={{ y: starY }} className="pointer-events-none absolute inset-0">
        <Starburst className="absolute top-32 right-[8%] h-16 w-16 text-jet-teal/25" />
        <Starburst className="absolute bottom-24 left-[6%] h-10 w-10 text-jet-amber/30" />
        <Starburst className="absolute top-1/2 right-[42%] h-6 w-6 text-jet-teal/20" />
      </motion.div>

      <div className="max-w-[88rem] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-jet-teal/30 bg-jet-teal/5 px-4 py-1.5 mb-6"
          >
            <Starburst className="h-3.5 w-3.5 text-jet-amber" />
            <span className="text-xs md:text-sm font-bold uppercase tracking-[0.15em] text-jet-teal-deep">
              DFW's Apartment Turnover Specialists
            </span>
          </motion.div>

          <h1 className="font-display font-semibold text-jet-ink text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.92] tracking-tight">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, ease, delay: 0.15 + i * 0.12 }}
                >
                  {i === 1 ? (
                    <>
                      Can Count <span className="text-jet-teal">On.</span>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-7 max-w-xl text-base md:text-lg text-jet-ink/70 leading-relaxed"
          >
            Reliable residential, commercial, and apartment cleaning with clear
            communication, dependable scheduling, and exceptional attention to detail.
            Proudly serving Dallas/Fort Worth, Texas.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.62 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <Button
              onClick={() => openLead("quote")}
              className="rounded-full bg-jet-teal hover:bg-jet-teal-deep text-white font-bold h-14 px-8 text-base teal-glow group"
              data-testid="hero-quote-button"
            >
              Request a Quote
              <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-10 flex flex-wrap gap-x-6 gap-y-2"
          >
            {BADGES.map((b) => (
              <div
                key={b}
                className="flex items-center gap-2 text-sm font-semibold text-jet-ink/60"
              >
                <Starburst className="h-3 w-3 text-jet-teal" />
                {b}
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease, delay: 0.3 }}
            className="relative"
          >
            <div className="relative overflow-hidden arch-mask shadow-2xl aspect-[3/4]">
              <motion.img
                style={{ y: imgY }}
                src={IMAGES.hero}
                alt="Spotless, freshly cleaned modern living space"
                className="absolute inset-0 h-[118%] w-full object-cover"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.6 }}
              className="absolute -left-4 bottom-10 bg-white/80 backdrop-blur-md rounded-2xl shadow-xl px-5 py-4 border border-white"
            >
              <div className="text-3xl font-display font-semibold text-jet-teal">24hr</div>
              <div className="text-xs font-bold uppercase tracking-wider text-jet-ink/60">
                Make-Ready<br />Turnaround*
              </div>
            </motion.div>
            <div className="absolute -right-3 top-8 grid place-items-center h-20 w-20 rounded-full bg-jet-amber text-white text-center shadow-xl rotate-6">
              <span className="text-[11px] font-bold uppercase leading-tight">
                Locally
                <br />
                Owned
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
